<template>
  <main class="guests-page">
    <div class="guests-page__head">
      <div>
        <h1 class="guests-page__title">Review qualità assistenziale</h1>
        <p class="guests-page__subtitle">
          Copertura delle attività assistenziali registrate e anomalie
          operative. Strumento gestionale di supporto al coordinamento — non
          valutazione del personale.
        </p>
      </div>
      <router-link to="/home" class="villa-btn villa-btn--ghost">
        <i class="fa-solid fa-arrow-left"></i> Dashboard
      </router-link>
    </div>

    <!-- Filtri -->
    <div class="guest-detail" style="margin-bottom: 14px">
      <div class="care-filters">
        <select v-model="facilityId" class="search-box" @change="load">
          <option value="">Seleziona struttura…</option>
          <option v-for="f in facilities" :key="f.id" :value="f.id">
            {{ f.name }}
          </option>
        </select>

        <div class="care-toggle">
          <button
            class="care-toggle__btn"
            :class="{ 'care-toggle__btn--on': mode === 'daily' && !custom }"
            @click="setMode('daily')"
          >
            Oggi
          </button>
          <button
            class="care-toggle__btn"
            :class="{ 'care-toggle__btn--on': mode === 'weekly' && !custom }"
            @click="setMode('weekly')"
          >
            Ultimi 7 giorni
          </button>
        </div>

        <label class="care-range">
          Dal
          <input v-model="from" type="date" class="search-box" @change="load" />
        </label>
        <label class="care-range">
          Al
          <input v-model="to" type="date" class="search-box" @change="load" />
        </label>

        <button
          class="villa-btn villa-btn--ghost"
          :disabled="loading || !facilityId"
          @click="load"
        >
          <i
            :class="
              loading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate'
            "
          ></i>
          Aggiorna
        </button>
      </div>
    </div>

    <div v-if="!facilityId" class="guests-loading">
      Seleziona una struttura per vedere la review.
    </div>
    <div v-else-if="loading" class="guests-loading">Caricamento…</div>
    <template v-else-if="summary">
      <p class="care-period">
        Periodo: <strong>{{ fmtDate(summary.from) }}</strong> –
        <strong>{{ fmtDate(summary.to) }}</strong>
      </p>

      <!-- KPI cards -->
      <div class="care-kpis">
        <div class="care-kpi">
          <span class="care-kpi__value">{{ t.activitiesCount }}</span>
          <span class="care-kpi__label">Attività registrate</span>
        </div>
        <div class="care-kpi">
          <span class="care-kpi__value">
            {{ t.guestsWithActivities }}/{{ t.activeGuests }}
          </span>
          <span class="care-kpi__label">Ospiti con attività</span>
        </div>
        <div
          class="care-kpi"
          :class="{ 'care-kpi--warn': t.guestsWithoutActivities }"
        >
          <span class="care-kpi__value">{{ t.guestsWithoutActivities }}</span>
          <span class="care-kpi__label">Ospiti senza attività</span>
        </div>
        <div class="care-kpi" :class="{ 'care-kpi--warn': t.toVerifyCount }">
          <span class="care-kpi__value">{{ t.toVerifyCount }}</span>
          <span class="care-kpi__label">Da verificare</span>
        </div>
        <div
          class="care-kpi"
          :class="{ 'care-kpi--warn': t.medicationNotDoneCount }"
        >
          <span class="care-kpi__value">{{ t.medicationNotDoneCount }}</span>
          <span class="care-kpi__label">Farmaci non somm./da verif.</span>
        </div>
        <div class="care-kpi" :class="{ 'care-kpi--warn': t.mealIssuesCount }">
          <span class="care-kpi__value">{{ t.mealIssuesCount }}</span>
          <span class="care-kpi__label">Pasti rifiutati/parziali</span>
        </div>
        <div
          class="care-kpi"
          :class="{ 'care-kpi--warn': t.stockWarningsCount }"
        >
          <span class="care-kpi__value">{{ t.stockWarningsCount }}</span>
          <span class="care-kpi__label">Avvisi stock presidi</span>
        </div>
      </div>

      <div class="care-grid">
        <!-- Breakdown per tipo attività -->
        <section class="care-block">
          <h3 class="detail-block-title">Per tipo attività</h3>
          <div v-if="!summary.byActionType.length" class="guests-loading">
            Nessuna attività nel periodo.
          </div>
          <table v-else class="guests-table">
            <tbody>
              <tr v-for="a in summary.byActionType" :key="a.actionType">
                <td>{{ a.label }}</td>
                <td style="text-align: right; font-weight: 600">
                  {{ a.count }}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- Breakdown per stato -->
        <section class="care-block">
          <h3 class="detail-block-title">Per stato</h3>
          <div v-if="!summary.byStatus.length" class="guests-loading">
            Nessuna attività nel periodo.
          </div>
          <table v-else class="guests-table">
            <tbody>
              <tr v-for="s in summary.byStatus" :key="s.status">
                <td>{{ s.label }}</td>
                <td style="text-align: right; font-weight: 600">
                  {{ s.count }}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- Breakdown per turno -->
        <section class="care-block">
          <h3 class="detail-block-title">Per turno</h3>
          <div v-if="!summary.byShift.length" class="guests-loading">
            Nessun dato turno.
          </div>
          <table v-else class="guests-table">
            <tbody>
              <tr v-for="s in summary.byShift" :key="s.shift">
                <td>
                  {{ s.shift === "non_indicato" ? "Non indicato" : s.shift }}
                </td>
                <td style="text-align: right; font-weight: 600">
                  {{ s.count }}
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>

      <!-- Copertura per ospite -->
      <section style="margin-top: 18px">
        <h3 class="detail-block-title">Copertura per ospite</h3>
        <div class="guests-table-wrap">
          <table class="guests-table">
            <thead>
              <tr>
                <th>Ospite</th>
                <th>Attività</th>
                <th>Da verificare</th>
                <th>Anomalie</th>
                <th>Ultima attività</th>
                <th class="actions-cell">Diario</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="g in summary.byGuest"
                :key="g.guestId"
                :class="{ 'care-row--empty': g.count === 0 }"
              >
                <td>{{ g.guestName }}</td>
                <td>
                  <span
                    v-if="g.count === 0"
                    class="status-badge status-badge--suspended"
                  >
                    nessuna
                  </span>
                  <span v-else>{{ g.count }}</span>
                </td>
                <td>{{ g.toVerify || "—" }}</td>
                <td>{{ g.issues || "—" }}</td>
                <td>
                  {{ g.lastActivityAt ? fmtDateTime(g.lastActivityAt) : "—" }}
                </td>
                <td class="actions-cell">
                  <router-link
                    class="icon-btn"
                    :to="`/guests/${g.guestId}`"
                    title="Apri cartella / diario ospite"
                  >
                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Anomalie -->
      <section style="margin-top: 18px">
        <h3 class="detail-block-title">
          Anomalie ({{ summary.anomalies.length }})
        </h3>
        <div v-if="!summary.anomalies.length" class="guests-loading">
          Nessuna anomalia nel periodo.
        </div>
        <ul v-else class="care-anomalies">
          <li v-for="(a, i) in summary.anomalies" :key="i">
            <span class="status-badge" :class="anomalyClass(a.type)">
              {{ anomalyLabel(a.type) }}
            </span>
            <router-link
              :to="`/guests/${a.guestId}`"
              class="care-anomaly__guest"
            >
              {{ a.guestName }}
            </router-link>
            <span v-if="a.performedAt" class="care-anomaly__when">
              · {{ fmtDateTime(a.performedAt) }}
            </span>
            <span v-if="a.actionType" class="care-anomaly__type">
              · {{ actionLabel(a.actionType) }}
            </span>
          </li>
        </ul>
      </section>
    </template>
  </main>
</template>

<script>
import { mapActions } from "vuex";

const ANOMALY_LABELS = {
  guest_without_activity: "Ospite senza attività",
  medication_not_done: "Farmaco non somministrato",
  medication_to_verify: "Farmaco da verificare",
  meal_refused: "Pasto rifiutato",
  meal_partial: "Pasto parziale",
  stock_warning: "Avviso stock presidi",
  activity_to_verify: "Attività da verificare",
};
const ACTION_LABELS = {
  room_cleaning: "Pulizia stanza",
  morning_hygiene: "Igiene mattutina",
  shower: "Doccia",
  diaper_change: "Cambio panno",
  meal_administration: "Pasto",
  medication_administration: "Farmaco",
  blood_glucose: "Glicemia",
  blood_pressure: "Pressione",
  oxygen_saturation: "Saturazione",
};

export default {
  name: "CareReviewPage",
  data() {
    return {
      facilities: [],
      facilityId: "",
      mode: "daily",
      from: "",
      to: "",
      summary: null,
      loading: false,
    };
  },
  computed: {
    t() {
      return this.summary ? this.summary.totals : {};
    },
    custom() {
      return !!(this.from || this.to);
    },
  },
  methods: {
    ...mapActions(["fetchFacilities", "fetchCareReviewSummary"]),
    anomalyLabel: (t) => ANOMALY_LABELS[t] || t,
    actionLabel: (t) => ACTION_LABELS[t] || t,
    anomalyClass(t) {
      if (t === "guest_without_activity" || t === "activity_to_verify") {
        return "status-badge--suspended";
      }
      return "status-badge--deceased";
    },
    fmtDate(d) {
      if (!d) return "—";
      const s = String(d).slice(0, 10).split("-");
      return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : d;
    },
    fmtDateTime(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return "—";
      const p = (n) => String(n).padStart(2, "0");
      return `${p(dt.getDate())}/${p(dt.getMonth() + 1)} ${p(
        dt.getHours()
      )}:${p(dt.getMinutes())}`;
    },
    setMode(m) {
      this.mode = m;
      this.from = "";
      this.to = "";
      this.load();
    },
    async load() {
      if (!this.facilityId) return;
      this.loading = true;
      try {
        const params = { facilityId: this.facilityId };
        if (this.from || this.to) {
          if (this.from) params.from = this.from;
          if (this.to) params.to = this.to;
        } else {
          params.period = this.mode;
        }
        this.summary = await this.fetchCareReviewSummary(params);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Caricamento review non riuscito."
        );
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    try {
      this.facilities = await this.fetchFacilities();
      if (this.facilities.length === 1) {
        this.facilityId = this.facilities[0].id;
        await this.load();
      }
    } catch {
      this.facilities = [];
    }
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style scoped>
.care-filters {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}
.care-toggle {
  display: inline-flex;
  gap: 4px;
}
.care-toggle__btn {
  border: 1px solid var(--villa-azure, #dceaf7);
  background: #fff;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: var(--villa-navy, #082b57);
  cursor: pointer;
}
.care-toggle__btn--on {
  background: var(--villa-navy, #082b57);
  color: #fff;
  border-color: var(--villa-navy, #082b57);
}
.care-range {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #475569;
}
.care-period {
  color: #475569;
  margin: 0 0 12px;
}
.care-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}
.care-kpi {
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.care-kpi--warn {
  border-color: #f59e0b;
  background: #fffbeb;
}
.care-kpi__value {
  font-size: 24px;
  font-weight: 700;
  color: var(--villa-navy, #082b57);
}
.care-kpi__label {
  font-size: 12px;
  color: #64748b;
}
.care-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
}
.care-block {
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 12px;
  padding: 14px;
}
.care-row--empty td {
  background: #fffbeb;
}
.care-anomalies {
  list-style: none;
  padding: 0;
  margin: 0;
}
.care-anomalies > li {
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.care-anomaly__guest {
  font-weight: 600;
  color: var(--villa-navy, #082b57);
}
.care-anomaly__when,
.care-anomaly__type {
  color: #64748b;
  font-size: 13px;
}
</style>
